'use client'

import type { TextProps } from './types'

import { cn } from '#lib/cn'

import { Text } from './text'

const SHIMMER_CLASS = 'ds-text-shimmer'
const SHIMMER_DURATION_SECONDS = 1.6

// Injected inline so the keyframe ships with the component — a Tailwind-processed
// stylesheet strips a stray keyframe. Honors `prefers-reduced-motion`.
// Position runs 200% → 0%: a larger value pulls the image left, so decreasing sweeps left-to-right.
const SHIMMER_STYLE = `
  @keyframes ${SHIMMER_CLASS}-sweep {
    from { background-position: 200% center; }
    to { background-position: 0% center; }
  }
  .${SHIMMER_CLASS} {
    background: linear-gradient(
      to right,
      var(--color-text-tertiary) 20%,
      var(--color-text-primary) 50%,
      var(--color-text-tertiary) 80%
    );
    background-size: 200% auto;
    background-clip: text;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    animation: ${SHIMMER_CLASS}-sweep ${SHIMMER_DURATION_SECONDS}s linear infinite;
  }
  @media (prefers-reduced-motion: reduce) {
    .${SHIMMER_CLASS} {
      animation: none;
      -webkit-text-fill-color: currentColor;
    }
  }
`

export interface ShimmerTextProps extends TextProps {
  /** Toggle the shimmer. When false, renders plain text. Defaults to `true`. */
  active?: boolean
}

/**
 * `<Text>` with an animated shimmer sweeping across it — for in-progress /
 * "generating" copy. Inactive, it's just a `<Text>`.
 */
export function ShimmerText({
  active = true,
  className,
  ...props
}: ShimmerTextProps) {
  if (!active) {
    return <Text className={className} {...props} />
  }

  return (
    <>
      <style>{SHIMMER_STYLE}</style>
      <Text className={cn(SHIMMER_CLASS, className)} {...props} />
    </>
  )
}
ShimmerText.displayName = 'ShimmerText'
